import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson, apiUpload } from './api';
import { useSEO } from './useSEO';

export default function AdminUpload() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const dropRef = useRef(null);

  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selected, setSelected] = useState({ level: '', subject: '', board: '', category: '', subfolder: '' });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [results, setResults] = useState([]);

  useSEO({
    title: 'Admin uploads',
    description: 'Upload PDFs and apply headers/footers automatically for Student Resource Hub.',
    canonicalPath: '/admin'
  });

  const resetBelow = (levelKey) => {
    setSelected((prev) => ({ ...prev, ...levelKey }));
  };

  const loadLevels = useCallback(async () => {
    try {
      const list = await apiJson('/api/levels');
      setLevels(list || []);
    } catch (err) {
      setError(err.message || 'Failed to load levels');
    }
  }, []);

  const loadSubjects = useCallback(async (level) => {
    if (!level) return setSubjects([]);
    try {
      const list = await apiJson(`/api/subjects/${encodeURIComponent(level)}`);
      setSubjects(list || []);
    } catch (err) {
      setError(err.message || 'Failed to load subjects');
    }
  }, []);

  const loadBoards = useCallback(async (level, subject) => {
    if (!level || !subject) return setBoards([]);
    try {
      const list = await apiJson(`/api/boards/${encodeURIComponent(level)}/${encodeURIComponent(subject)}`);
      setBoards(list || []);
    } catch (err) {
      setError(err.message || 'Failed to load boards');
    }
  }, []);

  const loadCategories = useCallback(async (level, subject, board) => {
    if (!level || !subject || !board) return setCategories([]);
    try {
      const data = await apiJson(`/api/resources/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}`);
      const opts = Object.keys(data || {});
      setCategories(opts);
    } catch (err) {
      setError(err.message || 'Failed to load categories');
    }
  }, []);

  useEffect(() => {
    if (user?.isAdmin) loadLevels();
  }, [user, loadLevels]);

  useEffect(() => {
    if (!selected.level) {
      setSubjects([]);
      setBoards([]);
      setCategories([]);
      return;
    }
    loadSubjects(selected.level);
  }, [selected.level, loadSubjects]);

  useEffect(() => {
    if (!selected.level || !selected.subject) {
      setBoards([]);
      setCategories([]);
      return;
    }
    loadBoards(selected.level, selected.subject);
  }, [selected.level, selected.subject, loadBoards]);

  useEffect(() => {
    if (!selected.level || !selected.subject || !selected.board) {
      setCategories([]);
      return;
    }
    loadCategories(selected.level, selected.subject, selected.board);
  }, [selected.level, selected.subject, selected.board, loadCategories]);

  const onFileInput = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming]);
    setStatus('');
  };

  const onDrop = (event) => {
    event.preventDefault();
    const incoming = Array.from(event.dataTransfer.files || []);
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming]);
    setStatus('');
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const selectionReady = useMemo(() => {
    return selected.level && selected.subject && selected.board && selected.category;
  }, [selected.level, selected.subject, selected.board, selected.category]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectionReady) {
      setError('Choose level, subject, board, and category first');
      return;
    }
    if (!files.length) {
      setError('Add at least one file');
      return;
    }

    setUploading(true);
    setError('');
    setStatus('Uploading…');
    setResults([]);

    const outcomes = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('level', selected.level);
        formData.append('subject', selected.subject);
        formData.append('board', selected.board);
        formData.append('category', selected.category);
        if (selected.subfolder) formData.append('subfolder', selected.subfolder);

        const res = await apiUpload('/api/admin/upload', formData, { token });
        outcomes.push({
          name: file.name,
          path: res.path,
          url: res.url,
          processed: !!res.processed,
        });
      }
      setResults(outcomes);
      setStatus(`Uploaded ${outcomes.length} file${outcomes.length === 1 ? '' : 's'}.`);
      setFiles([]);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h2>Admin uploads</h2>
          <p>You need to log in as an admin to access uploads.</p>
          <button className="primary-btn" onClick={() => navigate('/login')}>Log in</button>
        </div>
      </div>
    );
  }

  if (!user.isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h2>Admin uploads</h2>
          <p>This area is restricted to admin accounts. If you think this is a mistake, contact support.</p>
          <Link to="/" className="nav-link">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-card">
        <div className="admin-head">
          <div>
            <p className="muted">Uploads apply the header/footer script automatically for PDFs.</p>
            <h2>Upload to resources</h2>
          </div>
          {status && <span className="pill">{status}</span>}
        </div>

        <form className="admin-form" onSubmit={handleUpload}>
          <div className="admin-grid">
            <label className="admin-field">
              <span>Level</span>
              <select
                value={selected.level}
                onChange={(e) => {
                  resetBelow({ level: e.target.value, subject: '', board: '', category: '' });
                  setStatus('');
                }}
                required
              >
                <option value="">Select level</option>
                {levels.map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Subject</span>
              <select
                value={selected.subject}
                onChange={(e) => {
                  resetBelow({ subject: e.target.value, board: '', category: '' });
                  setStatus('');
                }}
                required
                disabled={!subjects.length}
              >
                <option value="">Select subject</option>
                {subjects.map((subj) => (
                  <option key={subj} value={subj}>{subj}</option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Board</span>
              <select
                value={selected.board}
                onChange={(e) => {
                  resetBelow({ board: e.target.value, category: '' });
                  setStatus('');
                }}
                required
                disabled={!boards.length}
              >
                <option value="">Select board</option>
                {boards.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Category / folder</span>
              <select
                value={selected.category}
                onChange={(e) => {
                  resetBelow({ category: e.target.value });
                  setStatus('');
                }}
                required
                disabled={!categories.length}
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span>Subfolder (optional)</span>
              <input
                type="text"
                placeholder="e.g., Paper 1 or Mark Schemes"
                value={selected.subfolder}
                onChange={(e) => setSelected((prev) => ({ ...prev, subfolder: e.target.value }))}
              />
            </label>
          </div>

          <div
            className="drop-zone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            ref={dropRef}
          >
            <p>Drag and drop files here, or choose files</p>
            <input type="file" multiple onChange={onFileInput} />
          </div>

          {files.length > 0 && (
            <div className="file-list">
              {files.map((file) => (
                <div key={file.name} className="file-chip">
                  <div>
                    <strong>{file.name}</strong>
                    <div className="muted">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  <button type="button" className="icon-btn" onClick={() => removeFile(file.name)}>Remove</button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}

          <div className="admin-actions">
            <button type="submit" className="primary-btn" disabled={uploading || !selectionReady}>
              {uploading ? 'Uploading…' : 'Upload and stamp PDFs'}
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setFiles([]);
                setError('');
                setStatus('');
              }}
              disabled={uploading}
            >
              Clear files
            </button>
          </div>
        </form>
      </div>

      {results.length > 0 && (
        <div className="admin-card">
          <h3>Upload results</h3>
          <div className="upload-results">
            {results.map((r) => (
              <div key={r.path} className="upload-result">
                <div className="upload-result-head">
                  <span className="pill">{r.processed ? 'PDF stamped' : 'Uploaded'}</span>
                  <span className="muted">{r.name}</span>
                </div>
                <div className="muted">{r.path}</div>
                <a href={r.url} target="_blank" rel="noreferrer" className="nav-link">Open file</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
